/**
 * Runtime module registry for the admin center.
 *
 * Every installable module contributes a manifest, optional translations, and
 * one or more Vue Router routes. The registry keeps those concerns in one
 * place so the core application can remain generic while feature modules stay
 * self-contained.
 */
import type { ModuleManifest, ModuleMenuItem } from '@airin/shared-types'
import type { RouteRecordRaw } from 'vue-router'

import { manifest as leadgenManifest } from './lead-generation/manifest'
import leadgenRoutes from './lead-generation/routes'

import { manifest as faqManifest } from './faq/manifest'
import faqRoutes from './faq/routes'

const moduleManifests: Map<string, ModuleManifest> = new Map()
const moduleRoutes: Map<string, RouteRecordRaw[]> = new Map()

moduleManifests.set('leadgen', leadgenManifest)
moduleRoutes.set('leadgen', leadgenRoutes)

moduleManifests.set('faq', faqManifest)
moduleRoutes.set('faq', faqRoutes)

/**
 * Returns the manifest for a single module key.
 */
export function getModuleManifest(moduleKey: string): ModuleManifest | undefined {
  return moduleManifests.get(moduleKey)
}

/**
 * Returns every registered module manifest.
 */
export function getAllModuleManifests(): ModuleManifest[] {
  return Array.from(moduleManifests.values())
}

/**
 * Builds menu items for active modules.
 *
 * Only items whose role configuration matches the current user are returned.
 */
export function getActiveModuleMenuItems(
  activeModuleKeys: string[],
  userRoles: string[]
): ModuleMenuItem[] {
  const menuItems: ModuleMenuItem[] = []

  for (const moduleKey of activeModuleKeys) {
    const manifest = moduleManifests.get(moduleKey)
    if (!manifest?.menuItems) continue

    for (const item of manifest.menuItems) {
      if (
        !item.roles ||
        item.roles.length === 0 ||
        item.roles.some((role) => userRoles.includes(role))
      ) {
        menuItems.push(item)
      }
    }
  }

  return menuItems.sort((a, b) => (a.order || 0) - (b.order || 0))
}

/**
 * Returns every registered module route with real component imports.
 */
export function getAllModuleRoutes(): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = []
  for (const moduleRouteList of moduleRoutes.values()) {
    routes.push(...moduleRouteList)
  }
  return routes
}

/**
 * Returns module-scoped translations so they can be merged into the main i18n instance.
 */
export function getAllModuleTranslations() {
  const translations: Record<string, any> = {}

  for (const manifest of moduleManifests.values()) {
    const moduleTranslations = (
      manifest as ModuleManifest & {
        translations?: { de: Record<string, any>; en: Record<string, any> }
      }
    ).translations

    if (moduleTranslations) {
      translations[manifest.key] = {
        de: {
          [manifest.key]: moduleTranslations.de
        },
        en: {
          [manifest.key]: moduleTranslations.en
        }
      }
    }
  }

  return translations
}

export default {
  getModuleManifest,
  getAllModuleManifests,
  getActiveModuleMenuItems,
  getAllModuleRoutes,
  getAllModuleTranslations
}
